'use client';

import {
  type AbstractIntlMessages,
  NextIntlClientProvider as IntlClientProvider,
} from 'next-intl';
import { type PropsWithChildren, useCallback } from 'react';

import { logger as browserLogger } from '@/logger/logger.browser';
import getLocaleFormatting from '@/utils/getLocaleFormatting';

interface NextIntlClientProviderProps extends PropsWithChildren {
  readonly locale: string;
  readonly messages: AbstractIntlMessages;
}
const NextIntlClientProvider = ({
  children,
  locale,
  messages,
}: NextIntlClientProviderProps) => {
  const handleError = useCallback(
    (err: any) => {
      // Ignore errors if the messages data is not ready yet.
      if (!messages || !Object.keys(messages).length) {
        return;
      }

      if (
        ['MISSING_TRANSLATION', 'MISSING_DATA', 'MISSING_MESSAGE'].includes(
          err.code,
        )
      ) {
        const regex = /"([^"\\]*(\\.[^"\\]*)*)"/g;
        const [message_key, localeKey] = err.message?.match(regex) ?? [];
        browserLogger.warn('ui_translation_error', {
          cause: JSON.stringify(err),
          message_key,
          locale: localeKey,
        });
      } else {
        throw err;
      }
    },
    [messages],
  );

  return (
    <IntlClientProvider
      locale={locale}
      messages={messages}
      timeZone="UTC" // default
      defaultTranslationValues={getLocaleFormatting}
      onError={handleError}
    >
      {children}
    </IntlClientProvider>
  );
};

export default NextIntlClientProvider;
